import { useEffect, useRef, useState } from "react";
import dayjs, { type Dayjs, type OpUnitType } from "dayjs";
import { ViewMode } from "@thepocman/gantt-task-react";

type UseTimelineControlsArgs = {
  firstEventDate: Date | null;
  initialViewMode?: ViewMode;
  initializeToFirstEvent?: boolean;
};

const getComparisonUnit = (viewMode: ViewMode): OpUnitType => {
  if (viewMode === ViewMode.Year) return "year";
  if (viewMode === ViewMode.Month) return "month";
  return "day";
};

export function useTimelineControls({
  firstEventDate,
  initialViewMode = ViewMode.Month,
  initializeToFirstEvent = false,
}: UseTimelineControlsArgs) {
  const [viewMode, setViewMode] = useState<ViewMode>(initialViewMode);
  const [viewDate, setViewDate] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [centerOnDate, setCenterOnDate] = useState<Date | undefined>(undefined);
  const [centerOnDateRequestId, setCenterOnDateRequestId] = useState(0);
  const hasInitializedDefaultView = useRef(false);

  const jumpToDate = (nextDate: Date) => {
    const normalizedDate = new Date(nextDate.getTime());
    setViewDate(normalizedDate);
    setCenterOnDate(normalizedDate);
    setCenterOnDateRequestId((current) => current + 1);
  };

  useEffect(() => {
    if (
      !initializeToFirstEvent ||
      !firstEventDate ||
      hasInitializedDefaultView.current
    ) {
      return;
    }

    const earliestDate = new Date(firstEventDate.getTime());
    setSelectedDate(dayjs(earliestDate));
    jumpToDate(earliestDate);
    hasInitializedDefaultView.current = true;
  }, [firstEventDate, initializeToFirstEvent]);

  const handleViewModeChange = (nextViewMode: ViewMode) => {
    setViewMode(nextViewMode);
    setCenterOnDate(undefined);
  };

  const handleTodayClick = () => {
    const today = dayjs();
    setSelectedDate(today);
    jumpToDate(today.toDate());
  };

  const handleEarliestClick = () => {
    if (!firstEventDate) return;

    const earliestDate = new Date(firstEventDate.getTime());
    setSelectedDate(dayjs(earliestDate));
    jumpToDate(earliestDate);
  };

  const handleDateChange = (nextDate: Dayjs | null) => {
    setSelectedDate(nextDate);

    if (!nextDate) {
      setCenterOnDate(undefined);
      return;
    }

    const nextJsDate = nextDate.toDate();
    jumpToDate(nextJsDate);
  };

  const handleHorizontalScroll = () => {
    setCenterOnDate(undefined);
    setSelectedDate(null);
  };

  const comparisonUnit = getComparisonUnit(viewMode);
  const isTodayActive =
    selectedDate !== null && dayjs().isSame(selectedDate, comparisonUnit);
  const isFirstEventActive =
    selectedDate !== null && firstEventDate !== null
      ? dayjs(firstEventDate).isSame(selectedDate, comparisonUnit)
      : false;

  return {
    viewMode,
    viewDate,
    selectedDate,
    centerOnDate,
    centerOnDateRequestId,
    isTodayActive,
    isFirstEventActive,
    handleViewModeChange,
    handleTodayClick,
    handleEarliestClick,
    handleDateChange,
    handleHorizontalScroll,
  };
}
